import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import { spawn } from 'redux-saga/effects';

import { EventPaymentActions, eventPaymentsKey, EventPaymentsState } from '@/store/event-payment/event-payment.actions';
import { eventPaymentReducer } from '@/store/event-payment/event-payment.reducers';
import {
    PServiceFiltersActions,
    pServiceFiltersKey,
    PServiceFiltersState,
} from '@/store/polako-service-filters/polako-service-filters.actions';
import {
    pServiceFiltersEffects,
    pServiceFiltersStorageMw,
} from '@/store/polako-service-filters/polako-service-filters.effects';
import { polakoServiceFiltersReducer } from '@/store/polako-service-filters/polako-service-filters.reducer';
import { PServiceActions, pServiceKey, PServiceState } from '@/store/polako-services/polako-services.actions';
import { pServiceEffects, pServiceQueryStringMw } from '@/store/polako-services/polako-services.effects';
import { pServicesReducer } from '@/store/polako-services/polako-services.reducer';

import { AuthActions, authKey, AuthState } from './auth/auth.actions';
import { authEffects } from './auth/auth.effects';
import { AuthReducer } from './auth/auth.reducer';
import {
    CalendarEventsActions,
    calendarEventsKey,
    CalendarEventsState,
} from './calendar-events/calendar-events.actions';
import { calendarEventsEffects } from './calendar-events/calendar-events.effects';
import { calendarEventsReducer } from './calendar-events/calendar-events.reducer';
import {
    CalendarFiltersActions,
    calendarFiltersKey,
    CalendarFiltersState,
} from './calendar-filters/calendar-filters.actions';
import { calendarFiltersEffects, calendarFiltersStorageMw } from './calendar-filters/calendar-filters.effects';
import { calendarFiltersReducer } from './calendar-filters/calendar-filters.reducer';
import {
    CalendarNavigationActions,
    calendarNavigationKey,
    CalendarNavigationState,
} from './calendar-navigation/calendar-navigation.actions';
import { calendarNavigationStorageMw } from './calendar-navigation/calendar-navigation.effects';
import { calendarNavigationReducer } from './calendar-navigation/calendar-navigation.reducer';

const sagaMiddleware = createSagaMiddleware();

interface StoreState {
    [authKey]: AuthState;
    [calendarEventsKey]: CalendarEventsState;
    [calendarFiltersKey]: CalendarFiltersState;
    [calendarNavigationKey]: CalendarNavigationState;
    [pServiceFiltersKey]: PServiceFiltersState;
    [pServiceKey]: PServiceState;
    [eventPaymentsKey]: EventPaymentsState;
}

type StoreActions = AuthActions &
    CalendarFiltersActions &
    CalendarEventsActions &
    CalendarNavigationActions &
    PServiceFiltersActions &
    PServiceActions &
    EventPaymentActions;

export const store = configureStore<StoreState, StoreActions>({
    reducer: {
        [authKey]: AuthReducer,
        [calendarEventsKey]: calendarEventsReducer,
        [calendarFiltersKey]: calendarFiltersReducer,
        [calendarNavigationKey]: calendarNavigationReducer,
        [pServiceFiltersKey]: polakoServiceFiltersReducer,
        [pServiceKey]: pServicesReducer,
        [eventPaymentsKey]: eventPaymentReducer,
    },
    // @ts-ignore
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActionPaths: ['payload.dateStart$', 'payload.dateEnd$', 'payload.cardsFirst$'],
            },
        }).concat([
            sagaMiddleware,
            calendarFiltersStorageMw,
            calendarNavigationStorageMw,
            pServiceFiltersStorageMw,
            pServiceQueryStringMw,
        ]),
});

function* rootSaga() {
    yield spawn(calendarFiltersEffects);
    yield spawn(calendarEventsEffects);
    yield spawn(authEffects);
    yield spawn(pServiceFiltersEffects);
    yield spawn(pServiceEffects);
}

sagaMiddleware.run(rootSaga);
